// Shopping Campaign Priority — Account Structure section, deterministic check.
// Campaigns grouped by feed; campaigns in an overlapping feed group that share one priority
// (no overlap-resolution rule) are flagged.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';

interface CampaignRow {
  id?: string;
  name: string;
  priorityLabel: string;
  feedLabel: string;
  merchantId: string;
  enableLocal: boolean;
}

interface FeedGroup {
  feedKey: string;
  feedLabel: string;
  merchantId: string;
  campaignCount: number;
  overlapping: boolean;
  undifferentiated: boolean;
  campaigns: CampaignRow[];
}

interface PriorityShape {
  totalCampaigns: number;
  overlappingGroupCount: number;
  undifferentiatedGroupCount: number;
  feedGroups: FeedGroup[];
}

export const ShoppingCampaignPriorityEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as PriorityShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Campaign priority &amp; feeds
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalCampaigns} Shopping campaigns
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.totalCampaigns.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Shopping campaigns</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.feedGroups.length.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Feed labels</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {e.undifferentiatedGroupCount.toLocaleString()}
            </div>
            <div className='audience-coverage__lbl'>Undifferentiated overlaps</div>
          </div>
        </div>
      </section>

      {e.feedGroups.map(g => (
        <section className='evidence__section' key={g.feedKey}>
          <h4 className='evidence__section-title'>
            Feed: {g.feedLabel}
            <span className='evidence__section-tag evidence__section-tag--context'>
              merchant {g.merchantId} · {g.campaignCount} campaign{g.campaignCount === 1 ? '' : 's'}
              {g.undifferentiated ? ' · same priority' : ''}
            </span>
          </h4>
          <table className='evidence__table evidence__table--shopping-priority'>
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Priority</th>
                <th>Local inventory</th>
              </tr>
            </thead>
            <tbody>
              {g.campaigns.map(c => (
                <tr
                  key={c.id ?? c.name}
                  className={classNames({ 'evidence__row--flagged': g.undifferentiated })}
                >
                  <td>{c.name}</td>
                  <td>{c.priorityLabel}</td>
                  <td>{c.enableLocal ? 'Yes' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}

      <p className='evidence__footnote'>
        Campaign priority (Low / Medium / High) decides which campaign wins when the same product is
        eligible in multiple campaigns on the same feed. It only matters within a feed that has more
        than one campaign — there, deliberately differentiated priorities (e.g. a catch-all set
        below specific campaigns) ensure the intended campaign takes the auction. Whether products
        actually overlap also depends on each campaign's inventory filters.
      </p>
    </div>
  );
};
